// Store CRO Audit — page entry component.
//
// Mirrors `tools/image-studio/ImageStudio.jsx` in shape, naming, and
// className conventions per locked decision #22. The hero card composition
// (`studio-hero` + `studio-hero-left` + `studio-hero-text` + `studio-hero-status`),
// op-tab pattern (`studio-op-tab`), and feedback button placement all
// match Image Studio so the visual rhythm is identical.
//
// Top-level state is the active view (registry vs run audit) and the
// FeedbackModal toggle. Everything else lives inside the two child
// components.

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SpaceBetween, Alert } from '@cloudscape-design/components';

import RegistryTable from './RegistryTable.jsx';
import RunAuditPanel from './RunAuditPanel.jsx';
import DashboardPanel from './DashboardPanel.jsx';
import FeedbackModal from './FeedbackModal.jsx';
import ThemeToggle from './ThemeToggle.jsx';
import { storeCROAuditApi } from './storeCROAuditApi.js';

import './cro-audit.css';

export default function StoreCROAudit() {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [error, setError] = useState('');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [username, setUsername] = useState('unknown');
  const registryRef = useRef(null);

  const scrollToRegistry = () => {
    registryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Lightweight summary used by the hero status row.
  const [registrySummary, setRegistrySummary] = useState({
    storeCount: 0,
    lastRunAt: null,
  });

  // Resolve the Harmony username once at mount (mirrors Image Studio's
  // approach — harmonyShim will have populated this by the time we render,
  // but we retry briefly in case it hasn't).
  useEffect(() => {
    let cancelled = false;
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    (async () => {
      let retries = 5;
      while (!cancelled && retries-- > 0) {
        const user = window?.harmony?.api?.getUserName?.();
        if (user) {
          if (!cancelled) setUsername(user);
          return;
        }
        await wait(300);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pull the registry summary once for the hero status pill. RegistryTable
  // also reads /api/registry on mount; this is a duplicate request but the
  // payload is small (single S3 GetObject behind the API) so the cost is
  // negligible vs the cleaner data flow.
  useEffect(() => {
    let cancelled = false;
    storeCROAuditApi
      .getRegistry()
      .then((data) => {
        if (cancelled) return;
        const stores = data?.stores || [];
        const sortedRuns = stores
          .map((s) => s.latest_run_at)
          .filter(Boolean)
          .sort()
          .reverse();
        setRegistrySummary({
          storeCount: stores.length,
          lastRunAt: sortedRuns[0] || null,
        });
      })
      .catch(() => {
        // Hero summary failures are silent — the registry table will surface
        // a real error if the API is genuinely down.
      });
    return () => {
      cancelled = true;
    };
  }, [refreshTrigger]);

  const handleAuditComplete = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  return (
    <div className="cro-audit-page">
      <div className="studio-container">
        <ThemeToggle />
        <Link
          to="/"
          className="studio-back-link"
          aria-label="Back to SmartBiz Toolkit"
        >
          ← Back to SmartBiz Toolkit
        </Link>

        {/* Hero — same composition as image-studio's */}
        <div className="studio-hero">
          <div className="studio-hero-left">
            <div className="studio-hero-icon">📊</div>
            <div className="studio-hero-text">
              <h1 className="studio-hero-title">Store CRO Audit</h1>
              <p className="studio-hero-subtitle">
                Score SmartBiz storefronts against the CRO checklist
              </p>
              <div className="studio-hero-status">
                <span className="studio-status-dot"></span>
                <span>{registrySummary.storeCount} stores in scope</span>
                <span className="studio-status-sep">|</span>
                <span>
                  {registrySummary.lastRunAt
                    ? `Last run: ${formatRelativeTime(registrySummary.lastRunAt)}`
                    : 'No runs yet'}
                </span>
              </div>
            </div>
          </div>

          {/* Feedback button — styled as op-tab for consistent height */}
          <div className="studio-hero-ops">
            <button
              type="button"
              className="studio-op-tab"
              onClick={() => setFeedbackOpen(true)}
              title="Share feedback about Store CRO Audit"
              aria-label="Share feedback"
            >
              <span className="studio-op-icon">💬</span>
              <span className="studio-op-label">Feedback</span>
              <span className="studio-op-desc">Share your thoughts</span>
            </button>
          </div>
        </div>

        <div className="studio-content">
          <SpaceBetween size="l">
            {error && (
              <Alert
                type="error"
                dismissible
                onDismiss={() => setError('')}
              >
                {error}
              </Alert>
            )}

            {/* Single-page layout: Dashboard → Run Audit → Registry */}
            <DashboardPanel refreshTrigger={refreshTrigger} />
            <RunAuditPanel
              username={username}
              onComplete={handleAuditComplete}
              onError={setError}
              onViewRegistry={scrollToRegistry}
            />
            <div ref={registryRef}>
              <RegistryTable
                refreshTrigger={refreshTrigger}
                onRefresh={() => setRefreshTrigger((n) => n + 1)}
                onError={setError}
                onSwitchToAudit={() => {}}
                username={username}
              />
            </div>
          </SpaceBetween>
        </div>
      </div>

      <FeedbackModal
        visible={feedbackOpen}
        onDismiss={() => setFeedbackOpen(false)}
        username={username}
      />
    </div>
  );
}

/** Human-friendly relative time. "2h ago", "3 days ago", etc. */
function formatRelativeTime(isoOrTimestamp) {
  if (!isoOrTimestamp) return '';
  // Run paths sometimes carry the dash-replaced ISO ("2026-06-23T04-12-09Z").
  // Normalise back to ISO before parsing.
  const normalized = isoOrTimestamp.replace(/(\d{2})-(\d{2})-(\d{2})Z$/, '$1:$2:$3Z');
  const dt = new Date(normalized);
  if (Number.isNaN(dt.getTime())) return isoOrTimestamp;
  const diffMs = Date.now() - dt.getTime();
  if (diffMs < 0) return 'just now';
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}
